import * as React from 'react'
import DialogTitle from '@mui/material/DialogTitle'
import Button from '@mui/material/Button'

import Grid from '@mui/material/Grid'

import DialogContent from '@mui/material/DialogContent'
import IconButton from '@mui/material/IconButton'
import CloseIcon from '@mui/icons-material/Close'
import DialogContentText from '@mui/material/DialogContentText'
import DialogActions from '@mui/material/DialogActions'
import Dialog from '@mui/material/Dialog'
import LoadingButton from '@mui/lab/LoadingButton'

import { renderImportOptionsMenu } from './SubImportMenuItem'

import Stepper from '@mui/material/Stepper'
import Step from '@mui/material/Step'
import StepLabel from '@mui/material/StepLabel'
import StepButton from '@mui/material/StepButton'

import Typography from '@mui/material/Typography'

import ArrowForwardIcon from '@mui/icons-material/ArrowForwardOutlined'
import EditableSelect from '../EditableSelect'
import TextField from '@mui/material/TextField'

import {
    useContextsStore,
    selectOwnedContexts,
} from '../../stores/useContextsStore'
import { UserContext } from '../../UserContext'

import { useTheme } from '@mui/material/styles'

import { SubImportContext } from './SubImportContext'
import { DialogsContext } from '../../DialogsContext'

import Select from '@mui/material/Select'
import MenuItem from '@mui/material/MenuItem'
import FormControl from '@mui/material/FormControl'
import InputLabel from '@mui/material/InputLabel'
import FormHelperText from '@mui/material/FormHelperText'
import FormControlLabel from '@mui/material/FormControlLabel'

import Switch from '@mui/material/Switch'
import Chip from '@mui/material/Chip'
import Box from '@mui/material/Box'

import axios from 'axios'
import qs from 'qs'

import { slugifyString } from '../../utils/index'
import { generateTextImportItems } from './TextImportItems'
import ImportingNotice from './ImportingNotice'
import { chipHoverAccent } from '../../theme'
import { importErrorMessage } from './importError'

const steps = ['Start', 'Sources', 'Settings', 'Results']

export default function ScholarImportDialog({
    presetContext = '',
    presetSearchString = '',
    open,
    onClose,
}) {
    const theme = useTheme() // hook to get the current theme

    const {
        activeSubImportDialog,
        setActiveSubImportDialog,
    } = React.useContext(SubImportContext)

    const {
        activeImportDialog,
        setActiveImportDialog,
        backLinkDialog,
        openImportDialog,
        closeImportDialog,
        setPresetSearchString,
    } = React.useContext(DialogsContext)

    // Shared across all import sources so the typed query persists when switching
    const searchString = presetSearchString
    const setSearchString = setPresetSearchString

    const [selectedContext, setSelectedContext] = React.useState(presetContext)

    const [errorMessage, setErrorMessage] = React.useState(null)

    const [importError, setImportError] = React.useState(null)

    const [completedSteps, setCompletedSteps] = React.useState([])

    const [visitedContext, setVisitedContext] = React.useState(false)

    const [numberOfResults, setNumberOfResults] = React.useState('40')

    const [hideSearchTerms, setHideSearchTerms] = React.useState(true)

    const [includeTitlesOnly, setIncludeTitlesOnly] = React.useState(false)

    const [sortByDate, setSortByDate] = React.useState(false)

    const [advancedSettings, setAdvancedSettings] = React.useState(false)

    const menuItems = generateTextImportItems()

    const [useForCategories, setUseForCategories] = React.useState('citations')

    const [modifyAnalyzedText, setModifyAnalyzedText] = React.useState('none')

    const [replaceEntities, setReplaceEntities] = React.useState(false)

    const filteredMenuItems = menuItems.filter(
        ({ textInput }) =>
            textInput.includes('googleScholar') ||
            textInput.includes('science') ||
            textInput.includes('googlePatents') ||
            textInput.includes(backLinkDialog)
    )

    const { username } = React.useContext(UserContext)
    const contexts = useContextsStore(selectOwnedContexts)
    const contextNames = contexts.map(c => c.name)

    const [activeStep, setActiveStep] = React.useState(2)
    const [skipped, setSkipped] = React.useState(new Set())

    const handleNext = () => {
        let newSkipped = skipped

        setActiveStep(prevActiveStep => prevActiveStep + 1)
        setSkipped(newSkipped)
    }

    const handleBack = () => {
        setActiveStep(prevActiveStep => prevActiveStep - 1)
    }

    const handleStep = step => {
        setActiveStep(step)
    }

    const handleNumberOfResultsChange = event => {
        setNumberOfResults(event.target.value)
    }

    const handleSearchStringChange = event => {
        setSearchString(event.target.value)
    }

    React.useEffect(() => {
        if (activeSubImportDialog.type) {
            setActiveStep(2)
            setActiveImportDialog(activeSubImportDialog.type)
        }
    }, [activeSubImportDialog])

    React.useEffect(() => {
        switch (activeStep) {
            case 0: // Start
                setActiveSubImportDialog(false)
                setActiveImportDialog('import')
                break
            case 1: // Sources
                setActiveSubImportDialog(false)
                setActiveImportDialog('shortTextImport')
                break
            case 2: // Settings
                if (!presetContext) {
                    setSelectedContext(generatePresetContext(searchString))
                }
                break
            case 3: // Analysis
                if (!searchString) {
                    setErrorMessage(
                        `Please, provide a search query you'd like to get results from Google Scholar for.`
                    )
                    setActiveStep(2)
                    break
                }
                if (!selectedContext) {
                    setErrorMessage(
                        'Please, select the graph to import your data to.'
                    )
                    setActiveStep(2)
                    break
                }
                if (selectedContext) {
                    setCompletedSteps([...completedSteps, 3])
                    setErrorMessage(null)
                    submitDataForm()
                    break
                }
        }
    }, [activeStep])

    const submitDataForm = () => {
        const dataToSend = {
            search: searchString,
            excludeSearchTerms: hideSearchTerms ? '1' : '',
            excludeAbstracts: includeTitlesOnly ? '1' : '',
            sortByDate: sortByDate ? '1' : '',
            context: selectedContext,
            limit: numberOfResults,
            categorizeBy: useForCategories,
            settings: 'morphemes',
            modifyAnalyzedText: modifyAnalyzedText,
            replaceEntities: replaceEntities,
        }

        const dataAsForm = qs.stringify(dataToSend)

        axios
            .post('/import/scholar', dataAsForm, {
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded', // Sending as URL-encoded form data
                },
            })
            .then(response => {
                if (response.data.redirectUrl) {
                    // If you're using window.location
                    window.location.href = response.data.redirectUrl

                    // Or, if you're using React Router's history (assuming you have access to the history object)
                    // history.push(response.data.redirectUrl);
                } else {
                    console.error(`Did't receive any response from the server.`)
                    if (response.data.error) {
                        setErrorMessage(
                            typeof response.data.error == 'object'
                                ? response.data.error.error ||
                                      JSON.stringify(response.data.error)
                                : response.data.error
                        )
                        setImportError(response.data.error)
                        setActiveStep(1)
                    }
                }
            })
            .catch(error => {
                // Handle the error here
                console.error('Received the following error', error)
                setErrorMessage(importErrorMessage(error))
                setImportError(response.data.error)
            })
    }

    return (
        <>
            <Dialog
                open={open}
                onClose={onClose}
                fullWidth={true}
                maxWidth="lg"
            >
                <DialogTitle
                    sx={{
                        padding: 4,
                        color: theme => theme.palette.text.primary,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                    }}
                >
                    {'Analyze Google Scholar search results'}
                    <IconButton
                        edge="end"
                        color="inherit"
                        onClick={onClose}
                        aria-label="close"
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <Stepper
                    activeStep={activeStep}
                    sx={{ paddingLeft: 4, paddingRight: 4, paddingTop: 2 }}
                >
                    {steps.map((label, index) => {
                        const stepProps = {}
                        const labelProps = {}

                        return (
                            <Step key={label} {...stepProps}>
                                <StepButton
                                    onClick={() => handleStep(index)}
                                    completed={(
                                        completedSteps.indexOf(index) >= 0
                                    ).toString()}
                                >
                                    <StepLabel
                                        {...labelProps}
                                        sx={{
                                            '& .MuiStepLabel-label': {
                                                fontSize: {
                                                    xs: '0.75rem',
                                                    sm: 'initial',
                                                },
                                            },
                                        }}
                                    >
                                        {label}
                                    </StepLabel>
                                </StepButton>
                            </Step>
                        )
                    })}
                </Stepper>

                {errorMessage && (
                    <Typography
                        sx={{
                            paddingRight: 4,
                            paddingLeft: 4,
                            paddingTop: 4,
                            paddingBottom: 0,
                        }}
                        color="error"
                        component="div"
                        gutterBottom
                    >
                        {errorMessage}
                    </Typography>
                )}

                {activeStep === 2 && (
                    <>
                        <DialogContent
                            sx={{
                                paddingRight: 4,
                                paddingLeft: 4,
                                paddingTop: 4,
                            }}
                        >
                            <Grid container spacing={8}>
                                <Grid item xs={12} sm={7}>
                                    <DialogContentText>
                                        Type in a search query to analyze
                                        related Google Scholar discourse:
                                    </DialogContentText>
                                    <FormControl
                                        fullWidth
                                        sx={{ marginTop: 4 }}
                                    >
                                        <TextField
                                            label={'Scholar Search Query'}
                                            value={searchString}
                                            variant="outlined"
                                            onChange={handleSearchStringChange}
                                            aria-describedby="searchString-helperText"
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    '& fieldset': {
                                                        borderWidth: 2,
                                                        borderColor:
                                                            'info.main',
                                                    },
                                                    '&:hover fieldset': {
                                                        borderWidth: 2,
                                                        borderColor:
                                                            'info.dark',
                                                    },
                                                    '&.Mui-focused fieldset': {
                                                        borderWidth: 2,
                                                    },
                                                },
                                            }}
                                        />
                                        {advancedSettings && (
                                            <FormHelperText
                                                id="searchString-helperText"
                                                sx={{ marginTop: 2 }}
                                            >
                                                You can also use Google's search
                                                operators, such as AND, OR,
                                                "phrase search", as well as
                                                site:noduslabs.com to search a
                                                specific website only or
                                                after:2021-03-21 to filter by
                                                time period
                                            </FormHelperText>
                                        )}
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                flexWrap: 'wrap',
                                                alignItems: 'center',
                                                gap: 1.5,
                                                mt: 2.5,
                                            }}
                                        >
                                            <Typography
                                                variant="body2"
                                                color="text.secondary"
                                                sx={{ fontWeight: 500 }}
                                            >
                                                try one of these:
                                            </Typography>
                                            {[
                                                'ai agents',
                                                'knowledge graphs',
                                                'heart rate variability',
                                                'psychedelic therapy',
                                            ].map(suggestion => (
                                                <Chip
                                                    key={suggestion}
                                                    label={suggestion}
                                                    size="medium"
                                                    variant="outlined"
                                                    onClick={() =>
                                                        setSearchString(
                                                            suggestion
                                                        )
                                                    }
                                                    sx={{
                                                        cursor: 'pointer',
                                                        fontSize: '0.85rem',
                                                        fontWeight: 500,
                                                        borderColor:
                                                            'rgba(0, 200, 200, 0.5)',
                                                        color: 'info.main',
                                                        '&:hover': {
                                                            color: chipHoverAccent,
                                                            borderColor: chipHoverAccent,
                                                        },
                                                    }}
                                                />
                                            ))}
                                        </Box>
                                    </FormControl>

                                    <FormControl
                                        fullWidth
                                        sx={{ marginTop: 6 }}
                                    >
                                        <DialogContentText>
                                            Using the import app:
                                        </DialogContentText>
                                        <DialogContent
                                            sx={{
                                                pl: 0,
                                                ml: 0,
                                                marginTop: -2,
                                            }}
                                        >
                                            {renderImportOptionsMenu(
                                                filteredMenuItems,
                                                'scholar'
                                            )}
                                        </DialogContent>
                                    </FormControl>
                                </Grid>
                                <Grid
                                    item
                                    xs={12}
                                    sm={4}
                                    sx={{
                                        marginTop: 3,
                                        marginBottom: 4,
                                    }}
                                >
                                    <FormControl
                                        fullWidth
                                        sx={{ marginTop: 4, marginBottom: 2 }}
                                    >
                                        <EditableSelect
                                            selectItems={contextNames}
                                            label={'Graph Name'}
                                            initialValue={selectedContext}
                                            onChange={name =>
                                                setSelectedContext(name)
                                            }
                                        />
                                    </FormControl>

                                    <FormControl
                                        fullWidth
                                        sx={{
                                            marginTop: 4,
                                            marginBottom: 2,
                                        }}
                                    >
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={advancedSettings}
                                                    onChange={event =>
                                                        setAdvancedSettings(
                                                            event.target.checked
                                                        )
                                                    }
                                                />
                                            }
                                            label="Show advanced settings"
                                        />
                                    </FormControl>
                                    {advancedSettings && (
                                        <>
                                            <FormControl
                                                fullWidth
                                                sx={{
                                                    marginTop: 4,
                                                    marginBottom: 2,
                                                }}
                                            >
                                                <InputLabel id="numberOfResults-label">
                                                    Number of Results to Analyze
                                                </InputLabel>
                                                <Select
                                                    labelId="numberOfResults-label"
                                                    id="numberOfResults"
                                                    value={numberOfResults}
                                                    onChange={
                                                        handleNumberOfResultsChange
                                                    }
                                                    label="Number of Results to Analyze"
                                                >
                                                    <MenuItem value={'10'}>
                                                        top 10 matches
                                                    </MenuItem>
                                                    <MenuItem value={'20'}>
                                                        top 20 matches
                                                    </MenuItem>
                                                    <MenuItem value={'30'}>
                                                        top 30 matches
                                                    </MenuItem>
                                                    <MenuItem value={'40'}>
                                                        top 40 matches
                                                    </MenuItem>
                                                    <MenuItem value={'60'}>
                                                        top 60 matches
                                                    </MenuItem>
                                                    <MenuItem value={'80'}>
                                                        top 80 matches
                                                    </MenuItem>
                                                    <MenuItem value={'100'}>
                                                        top 100 matches
                                                    </MenuItem>
                                                    <MenuItem value={'150'}>
                                                        top 150 matches
                                                    </MenuItem>
                                                    <MenuItem value={'200'}>
                                                        top 200 matches
                                                    </MenuItem>
                                                </Select>
                                            </FormControl>

                                            <FormControl
                                                fullWidth
                                                sx={{
                                                    marginTop: 4,
                                                    marginBottom: 2,
                                                }}
                                            >
                                                <InputLabel id="useForCategories-label">
                                                    Add Filter Categories
                                                </InputLabel>
                                                <Select
                                                    labelId="useForCategories-label"
                                                    id="useForCategories"
                                                    value={useForCategories}
                                                    onChange={event => {
                                                        setUseForCategories(
                                                            event.target.value
                                                        )
                                                    }}
                                                    label="Add Filter Categories"
                                                >
                                                    <MenuItem
                                                        value={'citations'}
                                                    >
                                                        by Citations
                                                    </MenuItem>
                                                    <MenuItem
                                                        value={'journals'}
                                                    >
                                                        by Journal Names
                                                    </MenuItem>
                                                    <MenuItem
                                                        value={
                                                            'citations_journals'
                                                        }
                                                    >
                                                        both by Citations and
                                                        Journals
                                                    </MenuItem>
                                                </Select>
                                            </FormControl>

                                            <FormControl
                                                fullWidth
                                                sx={{
                                                    marginTop: 4,
                                                    marginBottom: 2,
                                                }}
                                            >
                                                <InputLabel id="modifyAnalyzedText-label">
                                                    Build a knowledge graph from
                                                </InputLabel>
                                                <Select
                                                    labelId="modifyAnalyzedText-label"
                                                    id="modifyAnalyzedText"
                                                    value={modifyAnalyzedText}
                                                    onChange={event =>
                                                        setModifyAnalyzedText(
                                                            event.target.value
                                                        )
                                                    }
                                                    label="Build a knowledge graph from"
                                                >
                                                    <MenuItem
                                                        value={'none'}
                                                        key="1"
                                                    >
                                                        the words used in text
                                                    </MenuItem>
                                                    <MenuItem
                                                        value={'detectEntities'}
                                                        key="2"
                                                    >
                                                        the words and detected
                                                        entities
                                                    </MenuItem>
                                                    <MenuItem
                                                        value={
                                                            'extractEntitiesOnly'
                                                        }
                                                        key="3"
                                                    >
                                                        only the detected
                                                        entities
                                                    </MenuItem>
                                                </Select>
                                                {modifyAnalyzedText !=
                                                    'none' && (
                                                    <FormHelperText
                                                        sx={{ marginTop: 1 }}
                                                    >
                                                        By default, we extract
                                                        the words used in your
                                                        text. You can also
                                                        choose to detect and tag
                                                        entities in your text
                                                        and build a graph from
                                                        them.
                                                    </FormHelperText>
                                                )}
                                            </FormControl>
                                            {modifyAnalyzedText != 'none' && (
                                                <FormControl
                                                    fullWidth
                                                    sx={{
                                                        marginTop: 2,
                                                        marginBottom: 2,
                                                    }}
                                                >
                                                    <FormControlLabel
                                                        control={
                                                            <Switch
                                                                id="replaceEntities"
                                                                checked={
                                                                    replaceEntities
                                                                }
                                                                onChange={event =>
                                                                    setReplaceEntities(
                                                                        event
                                                                            .target
                                                                            .checked
                                                                    )
                                                                }
                                                            />
                                                        }
                                                        label="Replace entities with root concepts"
                                                    />
                                                    {replaceEntities && (
                                                        <FormHelperText
                                                            id="replaceEntities-helperText"
                                                            sx={{
                                                                marginTop: 1,
                                                            }}
                                                        >
                                                            When turned on, we
                                                            will not only detect
                                                            the entities, but
                                                            also replace them
                                                            with their
                                                            ontological root
                                                            concepts throughout
                                                            your text.
                                                        </FormHelperText>
                                                    )}
                                                </FormControl>
                                            )}
                                            <FormControl
                                                fullWidth
                                                sx={{
                                                    marginTop: 2,
                                                    marginBottom: 2,
                                                }}
                                            >
                                                <FormControlLabel
                                                    control={
                                                        <Switch
                                                            checked={
                                                                hideSearchTerms
                                                            }
                                                            onChange={event =>
                                                                setHideSearchTerms(
                                                                    event.target
                                                                        .checked
                                                                )
                                                            }
                                                        />
                                                    }
                                                    label="Hide Search Terms from Graph"
                                                />

                                                <FormHelperText>
                                                    explore the context around
                                                    the search query
                                                </FormHelperText>
                                            </FormControl>

                                            <FormControl
                                                fullWidth
                                                sx={{
                                                    marginTop: 4,
                                                    marginBottom: 2,
                                                }}
                                            >
                                                <FormControlLabel
                                                    control={
                                                        <Switch
                                                            checked={
                                                                includeTitlesOnly
                                                            }
                                                            onChange={event =>
                                                                setIncludeTitlesOnly(
                                                                    event.target
                                                                        .checked
                                                                )
                                                            }
                                                        />
                                                    }
                                                    label="Include Titles Only"
                                                />

                                                <FormHelperText>
                                                    analyze only the Research
                                                    paper titles
                                                </FormHelperText>
                                            </FormControl>

                                            <FormControl
                                                fullWidth
                                                sx={{
                                                    marginTop: 4,
                                                    marginBottom: 2,
                                                }}
                                            >
                                                <FormControlLabel
                                                    control={
                                                        <Switch
                                                            checked={sortByDate}
                                                            onChange={event =>
                                                                setSortByDate(
                                                                    event.target
                                                                        .checked
                                                                )
                                                            }
                                                        />
                                                    }
                                                    label="Sort by Date"
                                                />

                                                <FormHelperText>
                                                    sort papers by date, not by
                                                    relevance
                                                </FormHelperText>
                                            </FormControl>
                                        </>
                                    )}
                                </Grid>
                            </Grid>
                        </DialogContent>
                    </>
                )}

                {activeStep === 3 && (
                    <ImportingNotice
                        linkHref={`/${username}/${selectedContext}/edit`}
                    />
                )}
                <DialogActions
                    sx={{
                        paddingRight: 4,
                        paddingLeft: 4,
                        paddingBottom: 4,
                        paddingTop: 2,
                    }}
                >
                    {/* <Button onClick={onClose} variant="outlined">
                        Cancel
                    </Button> */}
                    <Button
                        color="inherit"
                        variant="outlined"
                        disabled={activeStep === 0}
                        onClick={handleBack}
                        sx={{ mr: 1, px: 3, py: 1, fontSize: '0.95rem' }}
                    >
                        Back
                    </Button>

                    <LoadingButton
                        color="info"
                        variant="contained"
                        size="large"
                        endIcon={<ArrowForwardIcon />}
                        disabled={!searchString.length > 0 || activeStep == 3}
                        onClick={handleNext}
                        sx={{ px: 4, py: 1, fontSize: '0.95rem' }}
                    >
                        {activeStep === 2 ? 'Visualize' : 'Next'}
                    </LoadingButton>
                </DialogActions>
            </Dialog>
        </>
    )
}

function generatePresetContext(searchString) {
    const dateAndTime = new Date()
        .toISOString()
        .slice(2, 16)
        .replace(/-/g, '')
        .replace(/:/g, '')

    if (!searchString) return `scholar_${dateAndTime}`

    const twoFirstSearchQueryWords = slugifyString(
        searchString
            .split(' ')
            .slice(0, 2)
            .join('_')
            .slice(0, 20)
            .replace(/[^\P{L}a-z][^a-z]*/giu, '')
    )

    return `scholar_${twoFirstSearchQueryWords}`
}
